import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

/* ── design tokens ──────────────────────────────────────────────────────── */
const STATUS = {
  NOT_CONNECTED: { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', dot: '#94a3b8' },
  PENDING:       { color: '#b45309', bg: '#fffbeb', border: '#fcd34d', dot: '#f59e0b' },
  VERIFIED:      { color: '#047857', bg: '#ecfdf5', border: '#6ee7b7', dot: '#10b981' },
  RESTRICTED:    { color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5', dot: '#ef4444' },
};

const C = {
  text:    '#0f172a',
  muted:   '#475569',
  subtle:  '#64748b',
  faint:   '#94a3b8',
  border:  '#e2e8f0',
  borderL: '#f1f5f9',
  bg:      '#f8fafc',
  white:   '#ffffff',
  blue:    '#2563eb',
  blueL:   '#eff6ff',
  blueBdr: '#bfdbfe',
};

const fmt = n =>
  n != null
    ? Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

/* ── shared style objects ────────────────────────────────────────────────── */
const card = {
  background: C.white,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: '20px 24px',
};

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '0 16px', height: 38, flexShrink: 0,
  background: C.blue, color: '#fff',
  border: 'none', borderRadius: 8,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const btnGhost = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 38, height: 38, flexShrink: 0,
  background: C.bg, color: C.muted,
  border: `1px solid ${C.border}`, borderRadius: 8,
  fontSize: 13, cursor: 'pointer',
};

/* ── Input with optional left prefix (e.g. "$", "CAD $") ─────────────────
   Uses alignItems:stretch so the input fills the full 40 px height.       */
function PrefixInput({ prefix, value, onChange, placeholder, type = 'text', min }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{
      display: 'flex', alignItems: 'stretch',
      height: 40, borderRadius: 8,
      border: `1px solid ${focus ? C.blue : C.border}`,
      background: focus ? C.white : C.bg,
      overflow: 'hidden', transition: 'border-color .15s',
    }}>
      {prefix && (
        <span style={{
          display: 'flex', alignItems: 'center',
          padding: '0 10px', flexShrink: 0,
          background: C.borderL, borderRight: `1px solid ${C.border}`,
          fontSize: 12, color: C.subtle, fontWeight: 500,
          userSelect: 'none', whiteSpace: 'nowrap',
        }}>
          {prefix}
        </span>
      )}
      <input
        value={value}
        onChange={onChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        type={type}
        min={min}
        placeholder={placeholder}
        style={{
          flex: 1, minWidth: 0,
          border: 'none', outline: 'none',
          background: 'transparent',
          padding: '0 12px',
          fontSize: 14, color: C.text,
          /* remove browser number spinners */
          WebkitAppearance: 'none',
          MozAppearance: 'textfield',
        }}
      />
    </div>
  );
}

function PlainInput({ value, onChange, placeholder, type = 'text' }) {
  const [focus, setFocus] = useState(false);
  return (
    <input
      value={value}
      onChange={onChange}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      type={type}
      placeholder={placeholder}
      style={{
        display: 'block', width: '100%', boxSizing: 'border-box',
        height: 40, padding: '0 12px',
        border: `1px solid ${focus ? C.blue : C.border}`,
        borderRadius: 8, outline: 'none',
        background: focus ? C.white : C.bg,
        fontSize: 14, color: C.text,
        transition: 'border-color .15s',
      }}
    />
  );
}

function Label({ children, hint }) {
  return (
    <label style={{ display: 'block', marginBottom: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{children}</span>
      {hint && <span style={{ fontSize: 11, color: C.faint, marginLeft: 6, fontWeight: 400 }}>{hint}</span>}
    </label>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
      <span
        onClick={() => onChange(!checked)}
        style={{
          width: 16, height: 16, borderRadius: 4, flexShrink: 0,
          border: `1.5px solid ${checked ? C.blue : '#cbd5e1'}`,
          background: checked ? C.blue : C.white,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all .15s',
        }}>
        {checked && <i className="fas fa-check" style={{ fontSize: 9, color: '#fff' }} />}
      </span>
      <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
    </label>
  );
}

function StatusPill({ status }) {
  const map = {
    paid:    { bg: '#ecfdf5', color: '#047857', dot: '#10b981' },
    failed:  { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444' },
    pending: { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20,
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 600,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════════════════════ */
export default function PartnerStripeConnect() {
  const { t } = useTranslation();
  const STATUS_LABEL = {
    NOT_CONNECTED: t('PartnerStripeConnect.statusNotConnected'),
    PENDING:       t('PartnerStripeConnect.statusPendingReview'),
    VERIFIED:      t('PartnerStripeConnect.statusVerified'),
    RESTRICTED:    t('PartnerStripeConnect.statusRestricted'),
  };
  const [tab, setTab]                   = useState('account');
  const [account, setAccount]           = useState(null);
  const [schedules, setSchedules]       = useState([]);
  const [payouts, setPayouts]           = useState([]);
  const [loadingAcct, setLoadingAcct]   = useState(true);
  const [onboarding, setOnboarding]     = useState(false);
  const [refreshing, setRefreshing]     = useState(false);
  const [onboardErr, setOnboardErr]     = useState('');
  const [splitAmount, setSplitAmount]   = useState('1000');
  const [split, setSplit]               = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);
  const [feeForm, setFeeForm] = useState({
    monthlyRetainer: '', orgId: '',
    includeLightScans: false, includeEvidenceStorage: false,
    lightScanUnitPrice: '', evidenceStoragePrice: '',
  });
  const [savingFee, setSavingFee] = useState(false);
  const [feeSaved, setFeeSaved]   = useState(false);

  const loadAccount = useCallback(() => {
    axiosInstance.get('/partner/stripe/account')
      .then(r => setAccount(r.data))
      .finally(() => setLoadingAcct(false));
  }, []);

  const loadSchedules = useCallback(() => {
    axiosInstance.get('/partner/stripe/fee-schedules').then(r => setSchedules(r.data));
  }, []);

  const loadPayouts = useCallback(() => {
    axiosInstance.get('/partner/stripe/payouts').then(r => setPayouts(r.data));
  }, []);

  useEffect(() => {
    loadAccount();
    if (new URLSearchParams(window.location.search).get('onboarded') === 'true') {
      axiosInstance.post('/partner/stripe/account/refresh').then(r => {
        setAccount(r.data);
        setLoadingAcct(false);
      });
    }
  }, [loadAccount]);

  useEffect(() => {
    if (tab === 'fees')    loadSchedules();
    if (tab === 'payouts') loadPayouts();
  }, [tab, loadSchedules, loadPayouts]);

  const handleOnboard = async () => {
    setOnboarding(true); setOnboardErr('');
    try {
      const r = await axiosInstance.post('/partner/stripe/onboard');
      window.location.href = r.data.url;
    } catch (e) {
      setOnboardErr(e.response?.data?.error || t('PartnerStripeConnect.onboardingFailed'));
      setOnboarding(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try { const r = await axiosInstance.post('/partner/stripe/account/refresh'); setAccount(r.data); }
    finally { setRefreshing(false); }
  };

  const handleSplitPreview = async () => {
    if (!splitAmount || Number(splitAmount) <= 0) return;
    setSplitLoading(true);
    try { const r = await axiosInstance.get(`/partner/stripe/split-preview?amount=${splitAmount}`); setSplit(r.data); }
    finally { setSplitLoading(false); }
  };

  const handleSaveFee = async () => {
    setSavingFee(true);
    try {
      await axiosInstance.post('/partner/stripe/fee-schedules', feeForm);
      setFeeSaved(true); loadSchedules();
      setTimeout(() => setFeeSaved(false), 2500);
    } finally { setSavingFee(false); }
  };

  const statusKey  = STATUS[account?.status] ? account.status : 'NOT_CONNECTED';
  const cfg        = STATUS[statusKey];
  const cfgLabel   = STATUS_LABEL[statusKey];
  const isVerified = !!account?.chargesEnabled;

  return (
    <div style={{ padding: '28px 32px', maxWidth: 900, margin: '0 auto', fontFamily: 'inherit' }}>

      {/* ── Page title ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{
            width: 36, height: 36, borderRadius: 9, flexShrink: 0,
            background: C.blueL, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="fas fa-credit-card" style={{ color: C.blue, fontSize: 15 }} />
          </span>
          <h1 style={{ margin: 0, fontSize: 21, fontWeight: 700, color: C.text, lineHeight: 1 }}>
            {t('PartnerStripeConnect.pageTitle')}
          </h1>
        </div>
        <p style={{ margin: '0 0 0 46px', fontSize: 13, color: C.subtle }}>
          {t('PartnerStripeConnect.pageSubtitle')}
        </p>
      </div>

      {/* ── Account status banner ──────────────────────────────────────────── */}
      {loadingAcct ? (
        <div style={{ height: 80, borderRadius: 12, background: C.bg, border: `1px solid ${C.border}`, marginBottom: 20 }} />
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', rowGap: 12, columnGap: 16,
          background: cfg.bg, border: `1px solid ${cfg.border}`,
          borderRadius: 12, padding: '14px 20px', marginBottom: 20,
        }}>
          {/* left: icon + text */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <span style={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              background: isVerified ? '#d1fae5' : '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className={`fas ${isVerified ? 'fa-check-circle' : 'fa-clock'}`}
                style={{ fontSize: 17, color: isVerified ? '#047857' : '#b45309' }} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{t('PartnerStripeConnect.accountCardTitle')}</div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 10px', marginTop: 3 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
                  <span style={{ fontSize: 12, color: cfg.color, fontWeight: 500 }}>{cfgLabel}</span>
                </span>
                {account?.chargesEnabled && <span style={{ fontSize: 12, color: C.subtle }}>{t('PartnerStripeConnect.chargesEnabled')}</span>}
                {account?.payoutsEnabled && <span style={{ fontSize: 12, color: C.subtle }}>{t('PartnerStripeConnect.payoutsEnabled')}</span>}
              </div>
              {account?.accountId && (
                <div style={{ fontSize: 11, color: C.faint, fontFamily: 'monospace', marginTop: 4 }}>
                  {account.accountId}
                </div>
              )}
            </div>
          </div>
          {/* right: buttons */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {!isVerified && (
              <button onClick={handleOnboard} disabled={onboarding}
                style={{ ...btnPrimary, opacity: onboarding ? .6 : 1 }}>
                <i className="fas fa-external-link-alt" style={{ fontSize: 10 }} />
                {onboarding ? t('PartnerStripeConnect.redirecting') : account?.accountId ? t('PartnerStripeConnect.continueSetup') : t('PartnerStripeConnect.connectStripe')}
              </button>
            )}
            <button onClick={handleRefresh} disabled={refreshing} title={t('PartnerStripeConnect.refreshStatus')}
              style={{ ...btnGhost, opacity: refreshing ? .6 : 1 }}>
              <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`} style={{ fontSize: 12 }} />
            </button>
          </div>
        </div>
      )}

      {/* error */}
      {onboardErr && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          fontSize: 13, color: '#b91c1c',
        }}>
          <i className="fas fa-exclamation-circle" />{onboardErr}
        </div>
      )}

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', borderBottom: `2px solid ${C.border}`, marginBottom: 24 }}>
        {[
          ['account', t('PartnerStripeConnect.tabOverview'),     'fa-chart-bar'],
          ['fees',    t('PartnerStripeConnect.tabFeeSchedules'), 'fa-file-invoice-dollar'],
          ['payouts', t('PartnerStripeConnect.tabPayouts'),      'fa-wallet'],
        ].map(([id, label, icon]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '10px 20px',
            border: 'none', background: 'none', cursor: 'pointer',
            borderBottom: `2px solid ${tab === id ? C.blue : 'transparent'}`,
            marginBottom: -2,
            fontSize: 13, fontWeight: tab === id ? 600 : 400,
            color: tab === id ? C.blue : C.subtle,
            whiteSpace: 'nowrap', transition: 'color .15s',
          }}>
            <i className={`fas ${icon}`} style={{ fontSize: 11 }} />
            {label}
          </button>
        ))}
      </div>

      {/* ══ Overview ══════════════════════════════════════════════════════════ */}
      {tab === 'account' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* stat chips — verified only */}
          {isVerified && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {[
                { label: t('PartnerStripeConnect.chipStatus'),   value: cfgLabel,                                     color: cfg.color,               icon: 'fa-id-card' },
                { label: t('PartnerStripeConnect.chipCharges'),  value: account?.chargesEnabled ? t('PartnerStripeConnect.enabled') : t('PartnerStripeConnect.pending'), color: account?.chargesEnabled ? '#047857' : '#b45309', icon: 'fa-bolt' },
                { label: t('PartnerStripeConnect.chipPayouts'),  value: account?.payoutsEnabled ? t('PartnerStripeConnect.enabled') : t('PartnerStripeConnect.pending'), color: account?.payoutsEnabled ? '#047857' : '#b45309', icon: 'fa-wallet' },
              ].map(({ label, value, color, icon }) => (
                <div key={label} style={{ ...card, padding: '14px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                    <i className={`fas ${icon}`} style={{ fontSize: 10, color: C.faint }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color }}>{value}</div>
                </div>
              ))}
            </div>
          )}

          {/* connect CTA */}
          {!isVerified && (
            <div style={{ ...card, background: C.blueL, border: `1px solid ${C.blueBdr}`, textAlign: 'center', padding: '40px 32px' }}>
              <span style={{ width: 56, height: 56, borderRadius: '50%', background: '#dbeafe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <i className="fas fa-credit-card" style={{ fontSize: 22, color: C.blue }} />
              </span>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>{t('PartnerStripeConnect.ctaTitle')}</div>
              <div style={{ fontSize: 13, color: C.muted, maxWidth: 360, margin: '0 auto 20px' }}>
                {t('PartnerStripeConnect.ctaDescription')}
              </div>
              <button onClick={handleOnboard} disabled={onboarding}
                style={{ ...btnPrimary, opacity: onboarding ? .6 : 1 }}>
                <i className="fas fa-external-link-alt" style={{ fontSize: 10 }} />
                {onboarding ? t('PartnerStripeConnect.redirectingToStripe') : t('PartnerStripeConnect.setUpPayouts')}
              </button>
            </div>
          )}

          {/* split-payment calculator */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <i className="fas fa-calculator" style={{ fontSize: 13, color: C.subtle }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{t('PartnerStripeConnect.splitCalculatorTitle')}</span>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <PrefixInput
                  prefix="CAD $"
                  value={splitAmount}
                  onChange={e => { setSplitAmount(e.target.value); setSplit(null); }}
                  type="number"
                  min="0"
                  placeholder="1000"
                />
              </div>
              <button onClick={handleSplitPreview} disabled={splitLoading || !splitAmount}
                style={{ ...btnPrimary, opacity: splitLoading || !splitAmount ? .6 : 1 }}>
                {splitLoading
                  ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 11 }} /> {t('PartnerStripeConnect.calculating')}</>
                  : <><i className="fas fa-calculator" style={{ fontSize: 11 }} /> {t('PartnerStripeConnect.calculate')}</>}
              </button>
            </div>

            {split ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                {[
                  { label: t('PartnerStripeConnect.grossCharge'),                         val: `$${fmt(split.chargeAmount)}`, color: C.text,    bg: C.bg,      bdr: C.border },
                  { label: t('PartnerStripeConnect.platformFee', { pct: split.platformFeePct }), val: `−$${fmt(split.platformFee)}`,  color: '#b91c1c', bg: '#fef2f2', bdr: '#fecaca' },
                  { label: t('PartnerStripeConnect.commission', { pct: split.commissionPct }),    val: `−$${fmt(split.commission)}`,   color: '#b45309', bg: '#fffbeb', bdr: '#fde68a' },
                  { label: t('PartnerStripeConnect.yourPayout'),                          val: `$${fmt(split.partnerNet)}`,   color: '#047857', bg: '#ecfdf5', bdr: '#6ee7b7' },
                ].map(({ label, val, color, bg, bdr }) => (
                  <div key={label} style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 10, padding: '14px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: C.subtle, marginBottom: 8, lineHeight: 1.4 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color }}>{val}</div>
                    <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>{split.currency}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, color: C.faint, background: C.bg, borderRadius: 8 }}>
                {t('PartnerStripeConnect.splitEmptyState')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ Fee Schedules ════════════════════════════════════════════════════ */}
      {tab === 'fees' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* existing */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
              {t('PartnerStripeConnect.activeSchedules')}
            </div>
            {schedules.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', padding: '40px 24px' }}>
                <i className="fas fa-file-invoice-dollar" style={{ fontSize: 28, color: C.border, marginBottom: 10, display: 'block' }} />
                <div style={{ fontSize: 13, color: C.faint }}>{t('PartnerStripeConnect.noFeeSchedules')}</div>
                <div style={{ fontSize: 12, color: C.border, marginTop: 4 }}>{t('PartnerStripeConnect.createUsingForm')}</div>
              </div>
            ) : schedules.map(fs => (
              <div key={fs.id} style={{ ...card, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{fs.orgName}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.blue, flexShrink: 0, marginLeft: 8 }}>${fmt(fs.monthlyRetainer)}</div>
                </div>
                <div style={{ fontSize: 12, color: C.subtle, marginBottom: 8 }}>{t('PartnerStripeConnect.currencyPerMonth', { currency: fs.currency })}</div>
                {(fs.includeLightScans || fs.includeEvidenceStorage) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {fs.includeLightScans && (
                      <span style={{ padding: '2px 8px', background: C.blueL, color: C.blue, borderRadius: 4, fontSize: 11, fontWeight: 500 }}>
                        <i className="fas fa-bolt" style={{ marginRight: 4 }} />{t('PartnerStripeConnect.perScanPrice', { price: fmt(fs.lightScanUnitPrice) })}
                      </span>
                    )}
                    {fs.includeEvidenceStorage && (
                      <span style={{ padding: '2px 8px', background: '#f5f3ff', color: '#7c3aed', borderRadius: 4, fontSize: 11, fontWeight: 500 }}>
                        <i className="fas fa-database" style={{ marginRight: 4 }} />{t('PartnerStripeConnect.perMonthPrice', { price: fmt(fs.evidenceStoragePrice) })}
                      </span>
                    )}
                  </div>
                )}
                {fs.effectiveFrom && <div style={{ fontSize: 11, color: C.faint, marginTop: 8 }}>{t('PartnerStripeConnect.effectiveFrom', { date: fs.effectiveFrom })}</div>}
              </div>
            ))}
          </div>

          {/* form */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <i className="fas fa-plus-circle" style={{ fontSize: 13, color: C.subtle }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{t('PartnerStripeConnect.addUpdateSchedule')}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <Label>{t('PartnerStripeConnect.monthlyRetainerLabel')}</Label>
                <PrefixInput prefix="$" value={feeForm.monthlyRetainer} type="number" min="0" placeholder="500"
                  onChange={e => setFeeForm(f => ({ ...f, monthlyRetainer: e.target.value }))} />
              </div>
              <div>
                <Label hint={t('PartnerStripeConnect.clientOrgIdHint')}>{t('PartnerStripeConnect.clientOrgIdLabel')}</Label>
                <PlainInput value={feeForm.orgId} type="number" placeholder={t('PartnerStripeConnect.clientOrgIdPlaceholder')}
                  onChange={e => setFeeForm(f => ({ ...f, orgId: e.target.value }))} />
              </div>
              <div style={{ borderTop: `1px solid ${C.borderL}`, paddingTop: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>
                  {t('PartnerStripeConnect.addOnCharges')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Checkbox label={t('PartnerStripeConnect.chargePerLightScan')} checked={feeForm.includeLightScans}
                    onChange={v => setFeeForm(f => ({ ...f, includeLightScans: v }))} />
                  {feeForm.includeLightScans && (
                    <div style={{ marginLeft: 24 }}>
                      <PrefixInput prefix="$" value={feeForm.lightScanUnitPrice} type="number" min="0"
                        placeholder={t('PartnerStripeConnect.perScanPricePlaceholder')}
                        onChange={e => setFeeForm(f => ({ ...f, lightScanUnitPrice: e.target.value }))} />
                    </div>
                  )}
                  <Checkbox label={t('PartnerStripeConnect.chargeEvidenceStorage')} checked={feeForm.includeEvidenceStorage}
                    onChange={v => setFeeForm(f => ({ ...f, includeEvidenceStorage: v }))} />
                  {feeForm.includeEvidenceStorage && (
                    <div style={{ marginLeft: 24 }}>
                      <PrefixInput prefix="$" value={feeForm.evidenceStoragePrice} type="number" min="0"
                        placeholder={t('PartnerStripeConnect.monthlyStoragePricePlaceholder')}
                        onChange={e => setFeeForm(f => ({ ...f, evidenceStoragePrice: e.target.value }))} />
                    </div>
                  )}
                </div>
              </div>
              <button onClick={handleSaveFee} disabled={savingFee || !feeForm.monthlyRetainer}
                style={{ ...btnPrimary, justifyContent: 'center', width: '100%', height: 40, opacity: savingFee || !feeForm.monthlyRetainer ? .6 : 1 }}>
                {savingFee  ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 11 }} /> {t('PartnerStripeConnect.saving')}</>
                : feeSaved  ? <><i className="fas fa-check" style={{ fontSize: 11 }} /> {t('PartnerStripeConnect.saved')}</>
                :              <><i className="fas fa-save" style={{ fontSize: 11 }} /> {t('PartnerStripeConnect.saveSchedule')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Payouts ══════════════════════════════════════════════════════════ */}
      {tab === 'payouts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!isVerified && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400e' }}>
              <i className="fas fa-exclamation-triangle" />
              {t('PartnerStripeConnect.completeOnboardingWarning')}
            </div>
          )}
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {payouts.length === 0 ? (
              <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <i className="fas fa-wallet" style={{ fontSize: 36, color: C.border, marginBottom: 14, display: 'block' }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: C.muted }}>
                  {isVerified ? t('PartnerStripeConnect.noPayoutsYet') : t('PartnerStripeConnect.connectToSeePayouts')}
                </div>
                {isVerified && <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>{t('PartnerStripeConnect.payoutsAppearHint')}</div>}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                    {[t('PartnerStripeConnect.thPayoutId'), t('PartnerStripeConnect.thAmount'), t('PartnerStripeConnect.thStatus'), t('PartnerStripeConnect.thArrivalDate'), t('PartnerStripeConnect.thDescription')].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p, i) => (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${C.borderL}`, background: i % 2 ? '#fafafa' : C.white }}>
                      <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 11, color: C.faint }}>{p.id}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: C.text }}>
                        ${fmt(p.amount)}{' '}
                        <span style={{ fontSize: 11, color: C.faint, fontWeight: 400 }}>{p.currency}</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}><StatusPill status={p.status} /></td>
                      <td style={{ padding: '12px 16px', color: C.muted, fontSize: 12 }}>
                        {p.arrivalDate
                          ? new Date(p.arrivalDate * 1000).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
                          : <span style={{ color: C.border }}>—</span>}
                      </td>
                      <td style={{ padding: '12px 16px', color: C.subtle, fontSize: 12 }}>
                        {p.description || <span style={{ color: C.border }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
